import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const HowItWorksSection: React.FC = () => {
  const { t } = useTranslation();
  
  const steps = [
    {
      step: t('howItWorks.steps.step1.number'),
      title: t('howItWorks.steps.step1.title'),
      description: t('howItWorks.steps.step1.description'),
      icon: '👤'
    },
    {
      step: t('howItWorks.steps.step2.number'), 
      title: t('howItWorks.steps.step2.title'),
      description: t('howItWorks.steps.step2.description'),
      icon: '⚙️'
    },
    {
      step: t('howItWorks.steps.step3.number'),
      title: t('howItWorks.steps.step3.title'),
      description: t('howItWorks.steps.step3.description'),
      icon: '📚'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {t('howItWorks.title')}
            <span className="bg-gradient-to-r from-green-500 to-blue-600 bg-clip-text text-transparent">
              {' '}{t('howItWorks.titleHighlight')}
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('howItWorks.subtitle')}
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              {/* Connector Line (hidden on last item) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-16 left-full w-full">
                  <div className="flex items-center">
                    <div className="flex-1 h-px bg-gradient-to-r from-gray-300 to-gray-100"></div>
                    <FaArrowRight className="text-gray-300 mx-4" />
                  </div>
                </div>
              )}
              
              <div className="text-center">
                {/* Step Icon */}
                <div className="relative mb-6">
                  <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-3xl shadow-lg">
                    {step.icon}
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {step.step}
                  </div>
                </div>
                
                {/* Step Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {step.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Benefits Grid */}
        <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 lg:p-12">
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {t('howItWorks.whyChoose.title')}
            </h3>
            <p className="text-gray-600">
              {t('howItWorks.whyChoose.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 text-xl">🤖</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{t('howItWorks.whyChoose.aiPowered.title')}</h4>
              <p className="text-sm text-gray-600">{t('howItWorks.whyChoose.aiPowered.description')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-green-600 text-xl">📊</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{t('howItWorks.whyChoose.personalized.title')}</h4>
              <p className="text-sm text-gray-600">{t('howItWorks.whyChoose.personalized.description')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-purple-600 text-xl">🎯</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{t('howItWorks.whyChoose.comprehensive.title')}</h4>
              <p className="text-sm text-gray-600">{t('howItWorks.whyChoose.comprehensive.description')}</p>
            </div>
            
            {/* <div className="text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-orange-600 text-xl">🌐</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{t('howItWorks.whyChoose.communityDriven.title')}</h4>
              <p className="text-sm text-gray-600">{t('howItWorks.whyChoose.communityDriven.description')}</p>
            </div> */}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12 space-y-4">
          <a
            href="https://christreignsmedia.io/register"
            className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-10 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            {t('hero.startJourney')}
          </a>
          <div>
            <Link 
              to="/technology" 
              className="inline-block text-blue-600 hover:text-blue-700 font-semibold border-b-2 border-transparent hover:border-blue-600 transition-all duration-300"
            >
              {t('howItWorks.learnMore')} →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;